/**
 * useSmartQueueWebSocket - Real-time Smart Queue Updates
 *
 * WebSocket hook for receiving real-time queue updates:
 * - queue.new_item: New action item added to queue
 * - queue.item_updated: Priority recalculated for existing item
 * - queue.item_removed: Item completed/dismissed
 * - queue.refresh: Full queue refresh needed
 *
 * SECURITY: Uses ticket-based authentication to prevent JWT exposure in URLs.
 *
 * Created: 2026-01-05
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { clientConfig } from '@/config';
import { WEBSOCKET } from '@/config/constants';
import { getAccessToken } from '@/services/api';
import { useSmartQueueStore } from '@/stores';
import type { QueueItem } from '@/types/queue';

interface QueueWebSocketMessage {
  type: 'queue.new_item' | 'queue.item_updated' | 'queue.item_removed' | 'queue.refresh';
  data?: QueueItem | { id: string };
  timestamp?: number;
}

interface UseSmartQueueWebSocketOptions {
  enabled?: boolean;
  onNewItem?: (item: QueueItem) => void;
  onItemUpdated?: (item: QueueItem) => void;
  onItemRemoved?: (id: string) => void;
  onRefresh?: () => void;
}

/**
 * Fetch a short-lived WebSocket ticket from the auth endpoint.
 * SECURITY: Tickets prevent JWT exposure in WebSocket URLs.
 */
async function fetchWebSocketTicket(): Promise<string | null> {
  const token = getAccessToken();
  if (!token) {
    console.error('[QueueWS] No auth token found');
    return null;
  }

  try {
    const response = await fetch(`${clientConfig.apiBaseUrl}/auth/ws-ticket`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('[QueueWS] Failed to get ticket:', response.status);
      return null;
    }

    const data = await response.json();
    return data.ticket;
  } catch (error) {
    console.error('[QueueWS] Error fetching ticket:', error);
    return null;
  }
}

export const useSmartQueueWebSocket = ({
  enabled = true,
  onNewItem,
  onItemUpdated,
  onItemRemoved,
  onRefresh,
}: UseSmartQueueWebSocketOptions = {}) => {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<QueueWebSocketMessage | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const handlersRef = useRef({ onNewItem, onItemUpdated, onItemRemoved, onRefresh });

  useEffect(() => {
    handlersRef.current = { onNewItem, onItemUpdated, onItemRemoved, onRefresh };
  }, [onNewItem, onItemUpdated, onItemRemoved, onRefresh]);

  // Get store actions for direct updates
  const addItem = useSmartQueueStore(state => state.addItem);
  const updateItem = useSmartQueueStore(state => state.updateItem);
  const removeItem = useSmartQueueStore(state => state.removeItem);
  const fetchQueue = useSmartQueueStore(state => state.fetchQueue);

  const connect = useCallback(async () => {
    if (!enabled) return;

    try {
      // Close existing connection
      if (wsRef.current) {
        wsRef.current.close();
      }

      // SECURITY: Fetch short-lived ticket instead of using JWT in URL
      const ticket = await fetchWebSocketTicket();
      if (!ticket) {
        console.error('[QueueWS] Failed to obtain ticket');
        // Retry with backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), WEBSOCKET.RECONNECT_MAX_DELAY_MS);
          reconnectTimeoutRef.current = window.setTimeout(() => {
            connect();
          }, delay);
        }
        return;
      }

      // Pass ticket (not JWT) in URL - ticket is single-use and expires in 60s
      // The FastAPI websocket router is mounted at /ws, outside the /api
      // REST prefix. Derive the API origin but do not carry a trailing /api
      // into the websocket path.
      // Keep two path segments after /ws so the generic /ws/{user_id}
      // endpoint cannot shadow the queue endpoint as user_id="queue".
      const wsUrl = `${clientConfig.websocketOrigin}/ws/queue/stream?ticket=${encodeURIComponent(ticket)}`;

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message: QueueWebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);

          switch (message.type) {
            case 'queue.new_item':
              if (message.data && 'priority' in message.data) {
                const item = message.data as QueueItem;
                addItem(item);
                handlersRef.current.onNewItem?.(item);
              }
              break;

            case 'queue.item_updated':
              if (message.data && 'priority' in message.data) {
                const item = message.data as QueueItem;
                updateItem(item);
                handlersRef.current.onItemUpdated?.(item);
              }
              break;

            case 'queue.item_removed':
              if (message.data && 'id' in message.data) {
                const { id } = message.data as { id: string };
                removeItem(id);
                handlersRef.current.onItemRemoved?.(id);
              }
              break;

            case 'queue.refresh':
              fetchQueue();
              handlersRef.current.onRefresh?.();
              break;
          }
        } catch (err) {
          console.error('Failed to parse Smart Queue WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        // React StrictMode intentionally mounts, cleans up, and remounts once
        // in development. Ignore events from the socket cleanup already
        // replaced; they are not connection failures and must not trigger a
        // reconnect storm or noisy console error.
        if (wsRef.current !== ws) return;
        console.error('Smart Queue WebSocket error:', error);
        setIsConnected(false);
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return;
        setIsConnected(false);
        wsRef.current = null;

        // Attempt reconnection with exponential backoff
        if (enabled && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), WEBSOCKET.RECONNECT_MAX_DELAY_MS);

          reconnectTimeoutRef.current = window.setTimeout(() => {
            connect();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to create Smart Queue WebSocket connection:', err);
      setIsConnected(false);
    }
  }, [enabled, addItem, updateItem, removeItem, fetchQueue]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    disconnect,
    reconnect: connect,
  };
};

export default useSmartQueueWebSocket;
